import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import { useCallback, useRef, useState } from 'react';
import { ClipStore, VideoState } from '../types';

// Generic video state setter factory following the recommended pattern
const createVideoSetter = (setVideoState: React.Dispatch<React.SetStateAction<VideoState>>) =>
  useCallback(
    (updates: Partial<VideoState>) => {
      setVideoState((prev) => ({ ...prev, ...updates }));
    },
    [setVideoState],
  );

export const useVideoPlayerState = (clipStore: ClipStore) => {
  const store = clipStore;

  // Use refs for values that change frequently to avoid re-renders
  const preloadingVideoRef = useRef<'video1' | 'video2' | null>(null);
  const video1ReadyRef = useRef(false);
  const video2ReadyRef = useRef(false);
  const lastVirtualTimestampRef = useRef(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  const previousClipIdsRef = useRef<string[]>([]);

  // State that needs to trigger re-renders
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [activeVideo, setActiveVideo] = useState<'video1' | 'video2'>('video1');

  // Combined video state
  const [videoState, setVideoState] = useState<VideoState>({
    currentClipIndex: 0,
    activeVideo: 'video1',
    video1Clip: null,
    video2Clip: null,
    video1Loading: false,
    video2Loading: false,
    video1TargetTime: undefined,
    video2TargetTime: undefined,
  });

  // UI state
  const [showHoverTime, setShowHoverTime] = useState(false);
  const [hoverTime, setHoverTime] = useState(0);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [timeInputValue, setTimeInputValue] = useState<string | null>(null);
  const [showSkipFeedback, setShowSkipFeedback] = useState<'forward' | 'backward' | null>(null);

  // Create the generic video setter using the factory pattern
  const updateVideoState = createVideoSetter(setVideoState);

  // Individual setters for backward compatibility
  const setVideo1Clip = useCallback(
    (clip: Clip | null) => {
      updateVideoState({ video1Clip: clip });
    },
    [updateVideoState],
  );

  const setVideo2Clip = useCallback(
    (clip: Clip | null) => {
      updateVideoState({ video2Clip: clip });
    },
    [updateVideoState],
  );

  const setVideo1Loading = useCallback(
    (loading: boolean) => {
      updateVideoState({ video1Loading: loading });
    },
    [updateVideoState],
  );

  const setVideo2Loading = useCallback(
    (loading: boolean) => {
      updateVideoState({ video2Loading: loading });
    },
    [updateVideoState],
  );

  const setVideo1TargetTime = useCallback(
    (targetTime: number | undefined) => {
      updateVideoState({ video1TargetTime: targetTime });
    },
    [updateVideoState],
  );

  const setVideo2TargetTime = useCallback(
    (targetTime: number | undefined) => {
      updateVideoState({ video2TargetTime: targetTime });
    },
    [updateVideoState],
  );

  const resetVideoState = useCallback(
    (
      options: {
        clearClips?: boolean;
      } = {},
    ) => {
      const { clearClips = false } = options;

      // Clear overlap triggers
      if (clearClips) {
        updateVideoState({
          video1Clip: null,
          video2Clip: null,
          video1TargetTime: undefined,
          video2TargetTime: undefined,
        });
      }

      // Reset refs
      preloadingVideoRef.current = null;
      video1ReadyRef.current = false;
      video2ReadyRef.current = false;
    },
    [updateVideoState],
  );

  const setupVideoClips = useCallback(
    (activeClipIndex: number, timeInClip: number = 0) => {
      const isActiveClipIndexValid = activeClipIndex >= 0 && activeClipIndex < store.clips.length;
      if (!isActiveClipIndexValid) {
        if (store.clips.length === 0) {
          setCurrentClipIndex(0);
          setActiveVideo('video1');
          updateVideoState({
            video1Clip: null,
            video2Clip: null,
            video1TargetTime: undefined,
            video2TargetTime: undefined,
          });
          return;
        }
        activeClipIndex = 0;
        timeInClip = 0;
      }

      setCurrentClipIndex(activeClipIndex);
      setActiveVideo('video1');

      const activeClip = store.clips[activeClipIndex];
      updateVideoState({
        video1Clip: activeClip,
        video1TargetTime: activeClip.startTime + timeInClip,
      });

      const nextIdx = activeClipIndex + 1;
      if (nextIdx < store.clips.length) {
        const nextClip = store.clips[nextIdx];
        updateVideoState({
          video2Clip: nextClip,
          video2TargetTime: nextClip.startTime,
        });
      } else {
        updateVideoState({
          video2Clip: null,
          video2TargetTime: undefined,
        });
      }
    },
    [store.clips, updateVideoState],
  );

  const showSkipFeedbackTemporarily = useCallback((direction: 'forward' | 'backward') => {
    setShowSkipFeedback(direction);
    setTimeout(() => {
      setShowSkipFeedback(null);
    }, 2000);
  }, []);

  return {
    // Store
    store,

    // Refs (for performance)
    preloadingVideoRef,
    video1ReadyRef,
    video2ReadyRef,
    lastVirtualTimestampRef,
    pauseStartTimeRef,
    previousClipIdsRef,

    // State
    currentClipIndex,
    setCurrentClipIndex,
    activeVideo,
    setActiveVideo,
    video1Clip: videoState.video1Clip,
    setVideo1Clip,
    video2Clip: videoState.video2Clip,
    setVideo2Clip,
    video1Loading: videoState.video1Loading,
    setVideo1Loading,
    video2Loading: videoState.video2Loading,
    setVideo2Loading,
    video1TargetTime: videoState.video1TargetTime,
    setVideo1TargetTime,
    video2TargetTime: videoState.video2TargetTime,
    setVideo2TargetTime,

    // UI State
    showHoverTime,
    setShowHoverTime,
    hoverTime,
    setHoverTime,
    hoverPosition,
    setHoverPosition,
    timeInputValue,
    setTimeInputValue,
    showSkipFeedback,
    setShowSkipFeedback,

    // Actions
    resetVideoState,
    setupVideoClips,
    showSkipFeedbackTemporarily,
  };
};
